/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {theme} from "../../../constants/theme";
import {useAppSelector} from '../../../redux/hooks';
import {selectCurrentUser} from '../../../redux/ducks/AccessControl/slice';
import {hasAccessToRoute} from "../../../redux/ducks/AccessControl/accessControlHelpers";

const SidebarLink = styled.div`
  display: flex;
  color: #e1e9fc;
  justify-content: left;
  align-items: center;
  padding: 20px;
  height: 60px;
  text-decoration: none;
  font-size: ${theme.layout.sidebar.body.menuItem.fontSize};

  &:hover {
    background: ${theme.layout.sidebar.body.menuItem.hoverColor};
    border-left: 4px solid #632ce4;
    cursor: pointer;
  }
`;

const SidebarLabel = styled.span`
  display: ${({isSidebarCollapsed}) => (isSidebarCollapsed ? 'none' : 'initial')};
`;
const SubSidebarLabel = styled.span`
  margin-left: 16px;
  justify-text: center;
  display: ${({isSidebarCollapsed}) => (isSidebarCollapsed ? 'none' : 'initial')};
`;
const DropdownLink = styled.div`
  height: 60px;
  padding-left: 3rem;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #f5f5f5;
  font-size: ${theme.layout.sidebar.body.menuItem.fontSize};

  &:hover {
    background: ${theme.layout.sidebar.body.menuItem.hoverColor};
    cursor: pointer;
  }
`;

const SubMenu = ({item, isSidebarCollapsed}) => {
  const [isSubNavOpen, setIsSubNavOpen] = useState(false);
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser)

  const showSubnav = () => setIsSubNavOpen(!isSubNavOpen);

  /**
   * Called when a menu item is clicked, navigates to the path if it exists, otherwise shows the subnav
   * @param item
   */
  const getPath = (item) => {
    if (item?.path) {
      navigate(item.path);
    }
      else if (item?.extLink){
      window.open(item.extLink,'_blank')
    }
      else if (item.subNav) {
      showSubnav();
    }
  }

  // for a given item, first check if it has a subnav and length > 0 (check if it has sub items).
  // if so, then recurse to see if any of the subnav items are accessible, and if so return true.
  // otherwise, check if the item itself is accessible.
  const shouldShowSidebarItem = (_item, _user) => {
    if (_item.subNav && _item.subNav.length > 0) {
      return _item.subNav.some(subItem => shouldShowSidebarItem(subItem, _user))
    } else {
      return hasAccessToRoute(_item.path ?? _item.extLink, _user)
    }
  }

  const hasSubNav = item.subNav && item.subNav.length > 0

  return (<>
    {/*level 1*/}
    {shouldShowSidebarItem(item, currentUser) &&
      <SidebarLink onClick={() => getPath(item)}>
        <div style={{width: "40px"}}>
          {item.icon}
        </div>
        <div>
          <SidebarLabel isSidebarCollapsed={isSidebarCollapsed}>{item.title}</SidebarLabel>
        </div>
        <div>
          {(hasSubNav && isSubNavOpen) ? item.iconOpened : hasSubNav ? item.iconClosed : null}
        </div>
      </SidebarLink>
    }
    {/*level 2*/}
    {isSubNavOpen && hasSubNav && item.subNav.map((item, index) => {
      return (
        shouldShowSidebarItem(item, currentUser) &&
        <DropdownLink onClick={() => getPath(item)} key={index}>
          {item.icon}
          <SubSidebarLabel isSidebarCollapsed={isSidebarCollapsed}>{item.title}</SubSidebarLabel>
        </DropdownLink>
      );
    })}
  </>);
};

export default SubMenu;
